import { isPlatformServer } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import {
  CreateGalleryBodyDto,
  CreateGalleryFromScratchBodyDto,
  DisplayUserDto,
  DuplicateGalleryBodyDto,
  GalleryDto,
  UpdateGalleryBodyDto,
} from '@picflow/types';
import { BehaviorSubject, Observable, Subject, firstValueFrom, lastValueFrom, tap } from 'rxjs';
import { GalleryLimitReachedDialogComponent } from '../account/dialogs/gallery-limit-reached-dialog/gallery-limit-reached-dialog.component';
import { NonPaymentDialogComponent } from '../account/dialogs/non-payment-dialog/non-payment-dialog.component';
import { ChecklistService } from '../checklist.service';
import { extraParamsForCreateGalleryFromPreset } from '../consts';
import { addIdPrefix } from '../lib/add-id-prefix';
import { debounce } from '../lib/debounce';
import { getAllRouteParams } from '../lib/get-all-route-params';
import { GetSubGalleryFields } from '../lib/new-gallery';
import { objectsMatch } from '../lib/objects-match';
import { randomString } from '../lib/random-string';
import { sleep } from '../lib/sleep';
import { unique } from '../lib/unique';
import { NonPaymentService } from '../non-payment.service';
import { AppConfigService } from '../providers/config.service';
import { AccessLevelService } from './access-level.service';
import { AnalyticsService } from './analytics.service';
import { DataLoaderService } from './data-loader.service';
import { DialogService } from './dialog.service';
import { FeatureFlagsService } from './feature-flags.service';
import { GalleryPasswordService } from './gallery-password.service';
import { SectionService } from './section.service';
import { SocketService } from './socket.service';
import { StateService } from './state.service';
import { StorageService } from './storage.service';
import { TenantService } from './tenant.service';

@Injectable({
  providedIn: 'root',
})
export class GalleryService {
  private viewedGalleries: string[] = [];
  private defaultIncludes = 'include[]=is_favourite&include[]=unseen_activities&include[]=tenant_product_name';
  private _updates: Subject<[string, string, GalleryDto]> = new BehaviorSubject<[string, string, GalleryDto]>(
    undefined
  );
  public readonly onUpdates: Observable<[string, string, GalleryDto]> = this._updates.asObservable();
  public ongoingUpdates: Set<string> = new Set();
  private galleryPathIsId: Set<string> = new Set();
  public galleryUpdateTimeMap: Map<string, number> = new Map();

  public onGalleryCreated$ = new Subject<GalleryDto>();

  public onGalleryUpdated$ = new Subject<GalleryDto>();

  public onGalleryDeleted$ = new Subject<GalleryDto>();

  constructor(
    private http: HttpClient,
    private config: AppConfigService,
    private loader: DataLoaderService,
    private state: StateService,
    private sectionService: SectionService,
    private galleryPasswordService: GalleryPasswordService,
    private featureFlags: FeatureFlagsService,
    private storage: StorageService,
    private tenantService: TenantService,
    private dialog: DialogService,
    private analytics: AnalyticsService,
    private checklistService: ChecklistService,
    private accessLevelService: AccessLevelService,
    private socketService: SocketService,
    private nonPaymentService: NonPaymentService,
    @Inject(PLATFORM_ID) private platformId: string
  ) {
    if (isPlatformServer(this.platformId)) return;
    this.defaultIncludes += '&include[]=parent_gallery_id';
    this.viewedGalleries = this.storage.get('viewed_galleries') || [];

    this.socketService.onSocketEvent.subscribe(event => {
      if (!event) return;
      if (['gallery.deleted', 'gallery.created', 'gallery.updated'].includes(event.name)) {
        this.loader.removeFuzzy('galleries');
        this.loader.removeFuzzy('folders');
      }

      const gallery = event.payload?.gallery as GalleryDto;

      if (event.name === 'gallery.deleted') {
        this._updates.next(['deleted', gallery.id, gallery]);
        this.onGalleryDeleted$.next(gallery);
      }

      if (event.name === 'gallery.created') {
        this._updates.next(['created', gallery.id, gallery]);
        this.onGalleryCreated$.next(gallery);
      }

      if (event.name === 'gallery.updated') {
        this.delayedUpdate(gallery);
        this.onGalleryUpdated$.next(gallery);
      }
    });
  }

  delayedUpdateMethods: Map<string, (...args) => unknown> = new Map();

  private delayedUpdate(gallery: GalleryDto) {
    if (!this.delayedUpdateMethods.has(gallery.id)) {
      this.delayedUpdateMethods.set(
        gallery.id,
        debounce(async (_gallery: GalleryDto) => {
          this._updates.next(['updated', gallery.id, _gallery]);
        }, 500)
      );
    }
    this.delayedUpdateMethods.get(gallery.id)(gallery);
  }

  public async getGalleryCount(): Promise<number> {
    const galleries = await this.getAllGalleries();
    return galleries.length;
  }

  public async getAllGalleries(
    options: {
      ignorePublicAccessLevel?: boolean;
      parameters?: { [key: string]: string };
      noSectionNone?: boolean;
    } = {}
  ): Promise<GalleryDto[]> {
    if (!options.parameters) options.parameters = {};
    if (!options.noSectionNone && !options.parameters.section) options.parameters.section = 'none';
    const key = `galleries?${JSON.stringify(options.parameters || {})}`;
    const resp = await this.loader.get(key, () => this.getGalleriesInternal(options.parameters));

    // The backend throws an error when sending a password
    resp.forEach(g => {
      this.galleryPasswordService.removePassword(g.id);
    });

    return await this.filterOutNoAccess(resp, { ignorePublicAccessLevel: options.ignorePublicAccessLevel });
  }

  public async getAllGalleriesIncludingSubgalleries(
    options: { ignorePublicAccessLevel?: boolean } = {}
  ): Promise<GalleryDto[]> {
    return this.getAllGalleries({
      noSectionNone: true,
      ignorePublicAccessLevel: options.ignorePublicAccessLevel,
    });
  }

  public async getSectionGalleries(sectionId: string, galleryId: string): Promise<GalleryDto[]> {
    return this.getAllGalleries({
      parameters: {
        section: sectionId,
        gallery: galleryId,
        password: this.getPasswordPlain(galleryId),
      },
    });
  }

  public async getSubGalleries(galleryId: string): Promise<GalleryDto[]> {
    const sections = await firstValueFrom(this.sectionService.getSections(galleryId));
    const gallerySections = sections.filter(s => s.object === 'gallery');
    const resp = await Promise.all(gallerySections.map(g => this.getSectionGalleries(g.id, galleryId)));
    return [].concat(...resp);
  }

  private galleryMatchesOptions(
    gallery: GalleryDto,
    options: { tenant?: boolean; ignore401Errors?: boolean; ignore503Errors?: boolean; expandCreatedBy?: boolean }
  ) {
    if (options.tenant && !gallery['tenant']?.id) return false;
    if (options.expandCreatedBy && !gallery['created_by']?.id) return false;
    return true;
  }

  private async findCachedGallery(
    galleryId: string,
    options: { tenant?: boolean; expandCreatedBy?: boolean },
    key: string
  ) {
    const cachedGallery = await this.loader.getFromCache<GalleryDto>(key);
    if (cachedGallery) return cachedGallery;
    const resps = (await this.loader.getFromCacheFuzzy<GalleryDto[]>('galleries?')) || [];
    for (const galleries of resps) {
      const gallery = galleries.find(g => g.id === galleryId);
      if (gallery && this.galleryMatchesOptions(gallery, options)) return gallery;
    }
  }

  public async getGallery(
    galleryId: string,
    options: { tenant?: boolean; ignore401Errors?: boolean; ignore503Errors?: boolean; expandCreatedBy?: boolean } = {}
  ): Promise<GalleryDto> {
    let key = `galleries_${galleryId}`;
    if (options.tenant) key += '_with-tenant';
    if (options.expandCreatedBy) key += '_expand-created-by';

    const cachedGallery = await this.findCachedGallery(galleryId, options, key);
    if (cachedGallery) return cachedGallery;

    return await this.loader.get(key, () => this.getGalleryInternal({ galleryId, ...options, key }));
  }

  public async getGalleryOwner(galleryId: string): Promise<DisplayUserDto> {
    const gallery = await this.getGallery(galleryId, { expandCreatedBy: true });
    return gallery['created_by'];
  }

  public async getTenantProfileGalleries(tenantId: string): Promise<GalleryDto[]> {
    const galleries = await this.getAllGalleries({ parameters: { tenant: tenantId } });
    return galleries.filter(g => g.show_in_profile);
  }

  public async getPublicFolderGalleries(folderId: string): Promise<GalleryDto[]> {
    const resp = await this.loader.get('galleries_folder' + folderId, () =>
      lastValueFrom(this.http.get<{ data: GalleryDto[] }>(`${this.config.apiEndpoint}/galleries?&folder=${folderId}`))
    );
    await this.onGetNewGalleries(resp.data);
    return await this.filterOutNoAccess(resp.data);
  }

  private async getGalleryByPath(
    path: string,
    options: { ignore401Errors?: boolean; ignore503Errors?: boolean } = {}
  ): Promise<GalleryDto> {
    return await this.loader.get(path, () => this.getGalleryByPathInternal({ path, ...options }));
  }

  public async getPublicGalleryFromSnapshot(
    next: ActivatedRouteSnapshot,
    options: { ignore401Errors?: boolean; ignore503Errors?: boolean; isPublic?: boolean } = {}
  ) {
    const params = getAllRouteParams(next);
    const idOrPath: string = params.galleryPath || addIdPrefix(params.galleryId, 'gal') || '';

    const reqOptions = {
      tenant: true,
      ...options,
    };

    if (this.galleryPathIsId.has(idOrPath)) {
      return await this.getGallery(idOrPath, reqOptions);
    }
    try {
      return await this.getGalleryByPath(idOrPath, options);
    } catch (err) {
      if (!(err instanceof HttpErrorResponse)) throw err;
      if (err.status !== 404 && err.status !== 403 && err.status !== 500) throw err;
      if (!idOrPath.startsWith('gal_')) throw err;
      // if error returns 404 then we can see if we can find it by id instead
      return await this.getGallery(idOrPath, reqOptions);
    }
  }

  public async createSubGallery(parentGallery: GalleryDto, parentSectionId?: string) {
    if (this.nonPaymentService.isBlocked()) {
      this.dialog.open(NonPaymentDialogComponent);
      return;
    }
    const params: CreateGalleryFromScratchBodyDto = GetSubGalleryFields(parentGallery);
    const section = await firstValueFrom(
      this.sectionService.getOrCreateSection('gallery', parentGallery.id, parentSectionId)
    );
    if (!section) return;
    params.section = section.id;
    const path = randomString(10);
    return this.createGalleryInternal({ path, ...params });
  }

  public async createGalleryFromPreset(preset: string): Promise<GalleryDto> {
    if (this.nonPaymentService.isBlocked()) {
      this.dialog.open(NonPaymentDialogComponent);
      return;
    }
    const path = randomString(10);
    return this.createGalleryInternal({ path, preset, ...extraParamsForCreateGalleryFromPreset });
  }

  public async duplicateGallery(galleryId: string): Promise<GalleryDto> {
    try {
      const params: DuplicateGalleryBodyDto = {
        copy_from: galleryId,
      };
      const gallery = <GalleryDto>await lastValueFrom(this.http.post(this.config.apiEndpoint + '/galleries', params));

      const updateParams: UpdateGalleryBodyDto = {};

      if (gallery.name) {
        updateParams.name = `${gallery.name} Copy`;
      } else {
        updateParams.title = `${gallery.title || 'Untitled'} Copy`;
      }
      await this.updateGallery(gallery.id, updateParams);
      this.analytics.track('gallery_duplicated');
      this._updates.next(['created', gallery.id, undefined]);
      return gallery;
    } catch (err) {
      this.handleCreateGalleryError(err);
    }
  }

  private async createGalleryInternal(params: CreateGalleryBodyDto) {
    try {
      const gallery = await lastValueFrom(this.http.post<GalleryDto>(this.config.apiEndpoint + '/galleries', params));
      this.analytics.track('gallery_created');
      this.loader.removeFuzzy('galleries');
      this._updates.next(['created', gallery.id, undefined]);
      this.onGalleryCreated$.next(gallery);
      return gallery;
    } catch (err) {
      this.handleCreateGalleryError(err);
    }
  }

  private async handleCreateGalleryError(err: Error) {
    if (!(err instanceof HttpErrorResponse)) throw err;
    if (err.status !== 400) throw err;
    if (err.error.code === 'upgrade_plan_required') {
      // plan must have been updated in the background
      this.dialog.open(GalleryLimitReachedDialogComponent, {});
      return;
    }
    console.log('Backend error, couldnt create gallery');
    await sleep(0);
    // eslint-disable-next-line no-debugger
    debugger;
    window.location.reload();
  }

  public async updateGallery(galleryId: string, params: UpdateGalleryBodyDto): Promise<GalleryDto> {
    this.galleryUpdateTimeMap.set(galleryId, Date.now());
    this.ongoingUpdates.add(galleryId);

    try {
      const [resp] = await Promise.all([
        lastValueFrom(
          this.http.patch<GalleryDto>(this.config.apiEndpoint + '/galleries/' + galleryId, params).pipe(
            tap(gallery => {
              gallery.updated_at += 1;
              this.onGalleryUpdated$.next(gallery);
            })
          )
        ),
        this.updateSubGalleries(galleryId, params),
      ]);
      this.loader.removeFuzzy('galleries');
      this.loader.removeFuzzy('folders');
      this.loader.removeFuzzy('galleries_section_');

      if (params.cover?.uuid) {
        this.checklistService.markItemAsDone('setCover').subscribe();
      }
      this.ongoingUpdates.delete(galleryId);
      this._updates.next(['updated', galleryId, resp]);
      return resp;
    } catch (err) {
      this.ongoingUpdates.delete(galleryId);
      throw err;
    }
  }

  public async refreshWatermark(galleryId): Promise<GalleryDto> {
    const params: UpdateGalleryBodyDto = { refresh_watermark: true };
    return lastValueFrom(this.http.patch<GalleryDto>(this.config.apiEndpoint + '/galleries/' + galleryId, params));
  }

  public async deleteGallery(gallery: GalleryDto) {
    await lastValueFrom(this.http.delete(this.config.apiEndpoint + '/galleries/' + gallery.id));
    this.loader.removeFuzzy('galleries');
    this.loader.removeFuzzy('folders');
    this.onGalleryDeleted$.next(gallery);
    this._updates.next(['deleted', gallery.id, undefined]);
    this.tenantService.refreshTenant();
  }
  public async triggerHackyUpdate(galleryId: string) {
    this._updates.next(['hackyUpdate', galleryId, undefined]);
  }

  public async favoriteGallery(gallery: GalleryDto) {
    await lastValueFrom(this.http.post(`${this.config.apiEndpoint}/galleries/${gallery.id}/favourite`, {}));
    this.loader.removeFuzzy('galleries');
  }

  public async unFavoriteGallery(gallery: GalleryDto) {
    await lastValueFrom(this.http.delete(`${this.config.apiEndpoint}/galleries/${gallery.id}/favourite`));
    this.loader.removeFuzzy('galleries');
  }

  public async fakeGalleryView(galleryId: string): Promise<void> {
    this.viewedGalleries.push(galleryId);
    this.storage.set('viewed_galleries', this.viewedGalleries, true);
  }

  public async galleryView(galleryId: string): Promise<void> {
    if (this.viewedGalleries.includes(galleryId)) return;
    if (this.featureFlags.get('blockGalleryViews')) return;
    const email = this.state.user?.email;
    if (email && ['@nusign.com', '@picflow.com'].find(_email => email.endsWith(_email))) {
      // preventing internal views as counting unless spesifically allowed
      if (!this.featureFlags.get('forceGalleryViews')) return;
    }
    this.viewedGalleries.push(galleryId);
    this.storage.set('viewed_galleries', this.viewedGalleries, true);
    await lastValueFrom(
      this.http.post(
        `${this.config.apiEndpoint}/galleries/${galleryId}/views?gallery=${galleryId}${this.getPassword(galleryId)}`,
        {}
      )
    );
  }

  public async onAllUpdatesDone(): Promise<void> {
    if (!this.ongoingUpdates.size) return;
    return new Promise(resolve => {
      this.onUpdates.subscribe(() => {
        if (!this.ongoingUpdates.size) resolve();
      });
    });
  }

  public async getSharedWithMeGalleries() {
    return this.galleriesSharedWithUser();
  }

  public async galleriesSharedWithUser(userId?: string) {
    const galleries = await this.loader.get('galleries_shared_' + userId, () =>
      this.galleriesSharedWithUserInternal(userId)
    );
    return await this.filterOutNoAccess(galleries, { ignoreMemberList: false });
  }

  private async galleriesSharedWithUserInternal(userId?: string) {
    let extra = '';
    if (this.state.activeTenant && userId) {
      extra = `&user=${userId}&tenant=${this.state.activeTenant.id}`;
    }
    const url = `${this.config.apiEndpoint}/galleries/shared?${this.defaultIncludes}${extra}`;
    const resp = <{ data: GalleryDto[] }>await lastValueFrom(this.http.get(url));
    const galleries = resp.data;

    const filteredGalleries = galleries.filter(gallery => {
      if (!gallery.section) return true;
      return !galleries.find(g => g.id === gallery['parent_gallery_id']);
    });
    await this.onGetNewGalleries(galleries);
    return unique(filteredGalleries, 'id');
  }

  private async updateSubGalleries(galleryId: string, params: UpdateGalleryBodyDto): Promise<void> {
    const subParams: UpdateGalleryBodyDto = {
      password: params.password,
      password_enabled: params.password_enabled,
    };

    if (!Object.values(subParams).filter(v => v !== undefined).length) return;
    const subGalleries = await this.getSubGalleries(galleryId);
    await Promise.all(
      subGalleries.map(gallery => {
        const _subParams = {
          ...subParams,
        };
        // When a gallery is made into a sub gallery, we currenty cant update the access levels to null so we just set them to be the same as the parent gallery
        // This means that, for now, while customers cant update the access levels of sub galleries, we need to update the sub gallery access levels
        // too when we update them for the main gallery
        if (gallery.tenant_access === null && params.tenant_access) _subParams.tenant_access = params.tenant_access;
        if (gallery.public_access === null && params.public_access) _subParams.public_access = params.public_access;
        return this.updateGallery(gallery.id, _subParams);
      })
    );
  }

  private async getGalleryInternal(options: {
    galleryId: string;
    tenant?: boolean;
    ignore401Errors?: boolean;
    ignore503Errors?: boolean;
    expandCreatedBy?: boolean;
    key: string;
  }) {
    let url = `${this.config.apiEndpoint}/galleries/${options.galleryId}?${this.defaultIncludes}${this.getPassword(
      options.galleryId
    )}`;
    if (options.tenant) url += '&expand[]=tenant';
    if (options.expandCreatedBy) url += '&expand[]=created_by';
    if (options.ignore401Errors) url += '&ignore_401_error=true';
    if (options.ignore503Errors) url += '&ignore_503_error=true';

    const gallery = await lastValueFrom(this.http.get<GalleryDto>(url));
    if (!gallery) {
      console.log('didnt get a gallery from the backend so manually throwing a 404');
      throw new HttpErrorResponse({ status: 404 });
    }

    const accessLevel = await this.accessLevelService.getAccessLevel(gallery);
    if (accessLevel === 'no_access') {
      console.log('got a gallery where we dont have the correct access level');
      throw new HttpErrorResponse({ status: 404 });
    }

    await this.onGetNewGalleries([gallery]);
    return gallery;
  }

  private async getGalleryByPathInternal(options: {
    path: string;
    ignore401Errors?: boolean;
    ignore503Errors?: boolean;
  }) {
    let url = `${this.config.apiEndpoint}/galleries/path/${options.path}?${this.defaultIncludes}&expand[]=tenant`;
    if (options.ignore401Errors) url += '&ignore_401_error=true';
    if (options.ignore503Errors) url += '&ignore_503_error=true';
    url += this.getPassword(options.path);
    const gallery = await lastValueFrom(this.http.get<GalleryDto>(url));

    if (!gallery) {
      console.log('didnt get a gallery from the backend so manually throwing a 404');
      throw new HttpErrorResponse({ status: 404 });
    }

    const accessLevel = await this.accessLevelService.getAccessLevel(gallery);
    if (accessLevel === 'no_access') {
      console.log('got a gallery where we dont have the correct access level');
      throw new HttpErrorResponse({ status: 404 });
    }

    if (gallery.password_enabled) {
      this.galleryPasswordService.setPassword(gallery.id, gallery['password']);
    }

    await this.onGetNewGalleries([gallery]);
    return gallery;
  }

  private async getGalleriesInternal(parameters?: { [key: string]: string }) {
    const url = `${this.config.apiEndpoint}/galleries?${this.defaultIncludes}${
      parameters
        ? '&' +
          Object.keys(parameters)
            .filter(key => parameters[key])
            .map(key => `${key}=${parameters[key]}`)
            .join('&')
        : ''
    }`;
    const resp = <{ data: GalleryDto[] }>await lastValueFrom(this.http.get(url));
    const galleries = resp.data;
    await this.onGetNewGalleries(galleries);
    return unique(galleries, 'id');
  }

  private getPassword(galleryId: string) {
    const password = this.galleryPasswordService.getPassword(galleryId);
    if (!password) return '';
    return '&password=' + encodeURIComponent(password);
  }

  private getPasswordPlain(galleryId: string) {
    const password = this.galleryPasswordService.getPassword(galleryId);
    if (!password) return '';
    return encodeURIComponent(password);
  }

  private async onGetNewGalleries(galleries: GalleryDto[]) {
    if (!galleries) return;

    const tenants: Set<string> = new Set();

    galleries.forEach(gallery => {
      this.galleryPathIsId.add(gallery.id);
      if (gallery['unseen_activities'] !== undefined && !this.state.unseenActivityCountMap.has(gallery.id)) {
        this.state.unseenActivityCountMap.set(gallery.id, gallery['unseen_activities']);
        delete gallery['unseen_activities'];
      }

      tenants.add(gallery['tenant']?.id || gallery['tenant']);

      // temp fix for galleries created without any contents in cover:
      if (!gallery.cover.overlay_color) {
        gallery.cover = extraParamsForCreateGalleryFromPreset.cover;
      }
    });

    const planNames = await Promise.all(
      Array.from(tenants).map(async tenantId => {
        const gallery =
          galleries.find(g => (g['tenant']?.id || g['tenant'] === tenantId) && !g.password_enabled && !g.section) ||
          galleries.find(g => (g['tenant']?.id || g['tenant'] === tenantId) && !g.password_enabled) ||
          galleries.find(g => g['tenant']?.id || g['tenant'] === tenantId);

        if (gallery.password_enabled && gallery['password']) {
          this.galleryPasswordService.setPassword(gallery.id, gallery['password']);
        }

        if (this.state.activeTenant?.id === tenantId && this.state.plan?.product) {
          galleries.forEach(g => (g['tenant'] = this.state.activeTenant));
          return {
            id: tenantId,
            planName: `Picflow ${this.state.plan.product.name}`,
          };
        }

        return {
          id: tenantId,
          planName: gallery.tenant_product_name || (await this.getPlanNameFromGalleryId(gallery, tenantId)),
        };
      })
    );

    galleries.forEach(gallery => {
      const planName = planNames.find(d => d.id === gallery['tenant']?.id || gallery['tenant']);
      gallery.tenant_product_name = planName.planName;
    });
  }

  private async filterOutNoAccess(
    galleries: GalleryDto[],
    _options: { ignoreMemberList?: boolean; ignorePublicAccessLevel?: boolean } = {}
  ): Promise<GalleryDto[]> {
    const options = { ignoreMemberList: true, ..._options };
    const allowedGalleries = await Promise.all(
      galleries.map(async gallery => {
        const accessLevel = await this.accessLevelService.getAccessLevel(gallery, {
          ignoreMemberList: options.ignoreMemberList,
          ignorePublicAccessLevel: options.ignorePublicAccessLevel,
        });
        if (accessLevel !== 'no_access') return gallery;
      })
    );
    return allowedGalleries.filter(Boolean);
  }

  public async checkForExternalUpdate(gallery: GalleryDto, newGallery?: GalleryDto) {
    if (!newGallery) newGallery = await this.getGallery(gallery.id, { tenant: true });
    const time = this.galleryUpdateTimeMap.get(gallery.id) || 0;
    if (time - newGallery.updated_at * 1000 > -1000) return;
    const same = objectsMatch(gallery, newGallery, [
      'updated_at',
      'unseen_activities',
      'last_changed_at',
      'tenant',
      'parent_gallery_id',
      'is_favourite',
      'tenant_product_name',
    ]);
    if (same) return;
    delete newGallery['tenant']; // tenant is a string in this case, not an object
    return { ...gallery, ...newGallery };
  }

  private async getPlanNameFromGalleryId(gallery: GalleryDto, tenantId: string, retryCount = 0): Promise<string> {
    return this.loader.get('plan-name_' + tenantId, async () => {
      const url = `${this.config.apiEndpoint}/galleries/${gallery.id}?a${this.getPassword(
        gallery.id
      )}&include[]=tenant_product_name&ignore_401_error=true`;
      try {
        const gallery = await lastValueFrom(this.http.get<GalleryDto>(url));
        return gallery.tenant_product_name;
      } catch (err) {
        if (retryCount > 2) throw err;
        await sleep(500);
        return this.getPlanNameFromGalleryId(gallery, tenantId, retryCount + 1);
      }
    });
  }
}
